import React, { useState, useEffect, useCallback } from 'react';
import {
  C, S, Badge, StatusBadge, FormField, ErrList, StatCard,
  PageHeader, FilterBar, DataTable, Modal, FormGrid,
  useToast, useConfirm, useCRUD,
} from '../../components/inventoryShared';
import {
  categoryController, subCategoryController, brandController,
  unitController, productController, warehouseController,
  locationController, supplierController, purchaseOrderController,
  purchaseReturnController, stockInController, stockOutController,
  stockTransferController, stockAdjustmentController,
  stockController, reportController,
} from '../../controllers/inventoryController';
import {
  Tag, Layers, Boxes, Settings, Package, BarChart3,
  PackageCheck, TrendingDown, ArrowLeftRight, ClipboardList,
  Users, ShoppingCart, RotateCcw, Warehouse, MapPin,
  AlertTriangle, Receipt, FileText, QrCode, Plus,
  Edit2, Trash2, Eye, Download, RefreshCw, CheckCircle,
  XCircle, Truck, ChevronDown, ChevronUp, Search,
} from 'lucide-react';
import ActionBtns from './ActionBtns';
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
// ─────────────────────────────────────────────────────────────────────────────
// 5. PRODUCTS PAGE
// ─────────────────────────────────────────────────────────────────────────────
export const ProductsPage = ({ companyId }) => {
  const crud = useCRUD(productController, companyId);
  const [showForm, setShowForm] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [catList, setCatList]   = useState([]);
  const [subCatList, setSubCatList] = useState([]);
  const [brandList, setBrandList]   = useState([]);
  const [unitList, setUnitList]     = useState([]);
  const [warehouseList, setWareList]= useState([]);
  const [errors, setErrors] = useState([]);

  const emptyForm = { name:'',sku:'',barcode:'',categoryId:'',subCategoryId:'',brandId:'',unitId:'',description:'',purchasePrice:0,sellingPrice:0,mrp:0,taxRate:0,hsnCode:'',minStock:0,maxStock:0,reorderLevel:0,reorderQty:0,weight:'',dimensions:'',warehouseId:'',status:'Active',tags:'',notes:'' };
  const [form, setForm] = useState(emptyForm);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  useEffect(() => {
    Promise.all([
      categoryController.getList(companyId),
      brandController.getList(companyId),
      unitController.getList(companyId),
      warehouseController.getList(companyId),
    ]).then(([cats, brands, units, wares]) => {
      cats.success   && setCatList(cats.data);
      brands.success && setBrandList(brands.data);
      units.success  && setUnitList(units.data);
      wares.success  && setWareList(wares.data);
    });
  }, [companyId]);

  useEffect(() => {
    if (form.categoryId) {
      subCategoryController.getByCategory(form.categoryId).then(r => r.success && setSubCatList(r.data));
    } else setSubCatList([]);
  }, [form.categoryId]);

  const openAdd  = () => { setEditItem(null); setForm(emptyForm); setErrors([]); setShowForm(true); };
  const openEdit = (r) => { setEditItem(r); setForm({...r, tags: r.tags?.join?.(', ') || r.tags || '' }); setErrors([]); setShowForm(true); };

  const handleSave = async () => {
    setSaving(true); setErrors([]);
    const payload = { ...form, companyId, tags: typeof form.tags === 'string' ? form.tags.split(',').map(t=>t.trim()).filter(Boolean) : form.tags };
    const res = editItem ? await productController.update(editItem.id, payload) : await productController.create(payload);
    setSaving(false);
    if (res.success) { crud.show(res.message); setShowForm(false); crud.fetchAll(); }
    else setErrors(res.errors?.length?res.errors:[res.message]);
  };

  const stockColor = (p) => p.currentStock <= 0 ? C.danger : p.currentStock <= p.minStock ? C.warning : C.success;
  const stockLabel = (p) => p.currentStock <= 0 ? 'Out of Stock' : p.currentStock <= p.minStock ? 'Low Stock' : 'In Stock';

  const columns = [
    { label:'Product', key:'name', render:(v,r) => (
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <div style={{ width:36, height:36, borderRadius:8, background:`${C.primary}12`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Package size={16} color={C.primary}/>
        </div>
        <div>
          <div style={{ fontWeight:700, fontSize:13 }}>{v}</div>
          <div style={{ fontSize:11, color:C.textMuted, fontFamily:'monospace' }}>{r.sku}</div>
        </div>
      </div>
    )},
    { label:'Category', key:'categoryName', render: v => v ? <Badge color={C.primary} bg={`${C.primary}12`}>{v}</Badge> : '—' },
    { label:'Brand', key:'brandName', render: v => v||'—' },
    { label:'Purchase', key:'purchasePrice', render: v => money(v) },
    { label:'MRP', key:'mrp', render: v => <span style={{ fontWeight:700 }}>{money(v)}</span> },
    { label:'Stock', key:'currentStock', render:(v,r) => (
      <div>
        <div style={{ fontWeight:700, color: stockColor(r) }}>{v} {r.unitName}</div>
        <div style={{ fontSize:11 }}><Badge color={stockColor(r)} bg={`${stockColor(r)}15`} dot>{stockLabel(r)}</Badge></div>
      </div>
    )},
    { label:'Status', key:'status', render: v => <StatusBadge status={v}/> },
    { label:'', key:'actions', render:(_, r) => <ActionBtns onView={()=>setViewItem(r)} onEdit={()=>openEdit(r)} onDelete={()=>crud.handleDelete(r.id,`"${r.name}"`)} /> },
  ];

  return (
    <div style={S.page}>
      {crud.ToastEl}{crud.Dialog}
      <PageHeader title="Products" subtitle="Manage product catalog" Icon={Package} color={C.primary}
        action={<button style={S.btnPri} onClick={openAdd}><Plus size={15}/> Add Product</button>} />
      <div style={S.card}>
        <div style={{ padding:'14px 14px 0' }}>
          <FilterBar search={crud.search} onSearch={crud.setSearch}
            filters={[
              { key:'categoryId', value:crud.filters.categoryId||'', onChange:v=>crud.setFilters(p=>({...p,categoryId:v})), options:[{value:'',label:'All Categories'},...catList.map(c=>({value:c.id||c._id,label:c.name}))] },
              { key:'status', value:crud.filters.status||'All', onChange:v=>crud.setFilters(p=>({...p,status:v==='All'?'':v})), options:[{value:'All',label:'All Status'},{value:'Active',label:'Active'},{value:'Inactive',label:'Inactive'}] },
            ]} />
        </div>
        <DataTable columns={columns} data={crud.data} loading={crud.loading} emptyTitle="No products found" onRowClick={setViewItem}
          page={crud.page} totalPages={crud.totalPages} onPageChange={crud.setPage} total={crud.total} />
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <Modal title={editItem?'Edit Product':'Add Product'} onClose={()=>setShowForm(false)} maxWidth={900}
          footer={<><button style={S.btnSec} onClick={()=>setShowForm(false)}>Cancel</button><button style={S.btnPri} onClick={handleSave} disabled={saving}>{saving?'Saving...':'Save Product'}</button></>}>
          <ErrList errors={errors} />

          <div style={S.secTitle}>Basic Information</div>
          <FormGrid>
            <FormField label="Product Name *"><input style={S.input} value={form.name} onChange={e=>set('name',e.target.value)} /></FormField>
            <FormField label="SKU *"><input style={S.input} value={form.sku} onChange={e=>set('sku',e.target.value)} placeholder="Auto-generated if blank" /></FormField>
            <FormField label="Barcode"><input style={S.input} value={form.barcode} onChange={e=>set('barcode',e.target.value)} /></FormField>
            <FormField label="HSN Code"><input style={S.input} value={form.hsnCode} onChange={e=>set('hsnCode',e.target.value)} /></FormField>
            <FormField label="Category *">
              <select style={S.select} value={form.categoryId} onChange={e=>set('categoryId',e.target.value)}>
                <option value="">Select Category</option>
                {catList.map(c=><option key={c.id||c._id} value={c.id||c._id}>{c.name}</option>)}
              </select>
            </FormField>
            <FormField label="Sub Category">
              <select style={S.select} value={form.subCategoryId} onChange={e=>set('subCategoryId',e.target.value)}>
                <option value="">Select Sub Category</option>
                {subCatList.map(c=><option key={c.id||c._id} value={c.id||c._id}>{c.name}</option>)}
              </select>
            </FormField>
            <FormField label="Brand">
              <select style={S.select} value={form.brandId} onChange={e=>set('brandId',e.target.value)}>
                <option value="">Select Brand</option>
                {brandList.map(b=><option key={b.id||b._id} value={b.id||b._id}>{b.name}</option>)}
              </select>
            </FormField>
            <FormField label="Unit">
              <select style={S.select} value={form.unitId} onChange={e=>set('unitId',e.target.value)}>
                <option value="">Select Unit</option>
                {unitList.map(u=><option key={u.id||u._id} value={u.id||u._id}>{u.name} ({u.shortName})</option>)}
              </select>
            </FormField>
            <FormField label="Status"><select style={S.select} value={form.status} onChange={e=>set('status',e.target.value)}><option>Active</option><option>Inactive</option></select></FormField>
            <FormField label="Tags (comma separated)"><input style={S.input} value={form.tags} onChange={e=>set('tags',e.target.value)} placeholder="tag1, tag2" /></FormField>
            <FormField label="Description" col={2}><textarea style={S.textarea} value={form.description} onChange={e=>set('description',e.target.value)} /></FormField>
          </FormGrid>

          <div style={S.divider} />
          <div style={S.secTitle}>Pricing</div>
          <FormGrid>
            <FormField label="Purchase Price (₹)"><input type="number" style={S.input} value={form.purchasePrice} onChange={e=>set('purchasePrice',e.target.value)} /></FormField>
            <FormField label="Selling Price (₹)"><input type="number" style={S.input} value={form.sellingPrice} onChange={e=>set('sellingPrice',e.target.value)} /></FormField>
            <FormField label="MRP (₹)"><input type="number" style={S.input} value={form.mrp} onChange={e=>set('mrp',e.target.value)} /></FormField>
            <FormField label="Tax Rate (%)"><input type="number" step="0.01" style={S.input} value={form.taxRate} onChange={e=>set('taxRate',e.target.value)} /></FormField>
          </FormGrid>

          <div style={S.divider} />
          <div style={S.secTitle}>Stock Settings</div>
          <FormGrid>
            <FormField label="Min Stock (Alert Level)"><input type="number" style={S.input} value={form.minStock} onChange={e=>set('minStock',e.target.value)} /></FormField>
            <FormField label="Max Stock"><input type="number" style={S.input} value={form.maxStock} onChange={e=>set('maxStock',e.target.value)} /></FormField>
            <FormField label="Reorder Level"><input type="number" style={S.input} value={form.reorderLevel} onChange={e=>set('reorderLevel',e.target.value)} /></FormField>
            <FormField label="Reorder Quantity"><input type="number" style={S.input} value={form.reorderQty} onChange={e=>set('reorderQty',e.target.value)} /></FormField>
            <FormField label="Default Warehouse">
              <select style={S.select} value={form.warehouseId} onChange={e=>set('warehouseId',e.target.value)}>
                <option value="">Select Warehouse</option>
                {warehouseList.map(w=><option key={w.id||w._id} value={w.id||w._id}>{w.name}</option>)}
              </select>
            </FormField>
          </FormGrid>

          <div style={S.divider} />
          <div style={S.secTitle}>Physical Details</div>
          <FormGrid>
            <FormField label="Weight"><input style={S.input} value={form.weight} onChange={e=>set('weight',e.target.value)} placeholder="e.g. 500g" /></FormField>
            <FormField label="Dimensions (L×W×H)"><input style={S.input} value={form.dimensions} onChange={e=>set('dimensions',e.target.value)} placeholder="e.g. 20×10×5 cm" /></FormField>
          </FormGrid>
        </Modal>
      )}

      {/* View Modal */}
      {viewItem && (
        <Modal title="Product Details" subtitle={`SKU: ${viewItem.sku}`} onClose={()=>setViewItem(null)} maxWidth={700}
          footer={<><button style={S.btnSec} onClick={()=>setViewItem(null)}>Close</button><button style={S.btnPri} onClick={()=>{setViewItem(null);openEdit(viewItem);}}>Edit</button></>}>
          <FormGrid>
            {[['Product Name',viewItem.name],['SKU',viewItem.sku],['Category',viewItem.categoryName||'—'],['Brand',viewItem.brandName||'—'],['Unit',viewItem.unitName||'—'],['HSN Code',viewItem.hsnCode||'—'],['Purchase Price',money(viewItem.purchasePrice)],['Selling Price',money(viewItem.sellingPrice)],['MRP',money(viewItem.mrp)],['Tax Rate',`${viewItem.taxRate}%`],['Current Stock',`${viewItem.currentStock} ${viewItem.unitName||''}`],['Min Stock',viewItem.minStock],['Status',viewItem.status]].map(([l,v])=>(
              <div key={l} style={{ background:'#f9fafb', borderRadius:8, padding:'10px 13px' }}>
                <div style={{ fontSize:11, color:C.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:3 }}>{l}</div>
                <div style={{ fontSize:13, fontWeight:600 }}>{v}</div>
              </div>
            ))}
          </FormGrid>
          {viewItem.description && <div style={{ background:'#f9fafb', borderRadius:8, padding:'10px 13px', marginTop:8 }}><div style={{ fontSize:11, color:C.textMuted, fontWeight:700, marginBottom:3 }}>DESCRIPTION</div><div style={{ fontSize:13 }}>{viewItem.description}</div></div>}
        </Modal>
      )}
    </div>
  );
};
export default ProductsPage;